import React, { useState } from 'react';
import { useQueue } from '../contexts/QueueContext';
import { useAuth } from '../contexts/AuthContext';
import { playlistApi } from '../services/apiService';
import { FaTimes, FaTrash, FaPlay, FaSave } from 'react-icons/fa';

interface QueueDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const QueueDrawer: React.FC<QueueDrawerProps> = ({ isOpen, onClose }) => {
  const { queue, currentQueueIndex, isQueueActive, removeFromQueue, clearQueue, playQueue } = useQueue();
  const { user } = useAuth();
  const [toast, setToast] = useState<string | null>(null);
  const [showNameInput, setShowNameInput] = useState(false);
  const [playlistName, setPlaylistName] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const handleSaveAsPlaylist = () => {
    if (!user) {
      showToast('Please sign in to save playlists.');
      return;
    }
    setShowNameInput(true);
    setPlaylistName('');
  };

  const handleConfirmSave = async () => {
    if (!playlistName.trim()) return;
    setIsSaving(true);

    try {
      const { playlist } = await playlistApi.create(playlistName.trim());
      for (const song of queue) {
        await playlistApi.addSong(playlist.id, song.id);
      }
      showToast('Playlist saved!');
      setShowNameInput(false);
    } catch (err: any) {
      showToast(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      {isOpen && <div style={overlayStyle} onClick={onClose} />}
      <div style={{ ...drawerStyle, transform: isOpen ? 'translateX(0)' : 'translateX(100%)' }}>
        <div style={headerStyle}>
          <h3 style={{ color: '#fff', margin: 0 }}>Queue ({queue.length})</h3>
          <button onClick={onClose} style={iconBtnStyle}><FaTimes /></button>
        </div>

        {toast && (
          <div style={toastStyle}>
            {toast}
          </div>
        )}

        {queue.length === 0 ? (
          <p style={emptyStyle}>Queue is empty. Add songs from any volume.</p>
        ) : (
          <>
            <div style={actionsStyle}>
              <button onClick={() => playQueue()} style={actionBtnStyle}>
                <FaPlay style={{ marginRight: '6px' }} /> Play All
              </button>
              <button onClick={handleSaveAsPlaylist} style={actionBtnStyle}>
                <FaSave style={{ marginRight: '6px' }} /> Save as Playlist
              </button>
              <button onClick={clearQueue} style={{ ...actionBtnStyle, borderColor: '#ff6b6b', color: '#ff6b6b' }}>
                <FaTrash style={{ marginRight: '6px' }} /> Clear
              </button>
            </div>

            {showNameInput && (
              <div style={nameInputContainerStyle}>
                <input
                  type="text"
                  placeholder="Playlist name"
                  value={playlistName}
                  onChange={(e) => setPlaylistName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleConfirmSave()}
                  style={nameInputStyle}
                  autoFocus
                />
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button
                    onClick={handleConfirmSave}
                    disabled={isSaving || !playlistName.trim()}
                    style={{ ...saveConfirmBtnStyle, opacity: isSaving || !playlistName.trim() ? 0.5 : 1 }}
                  >
                    {isSaving ? '...' : 'Save'}
                  </button>
                  <button onClick={() => setShowNameInput(false)} style={cancelBtnStyle}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div style={listStyle}>
              {queue.map((song, index) => {
                const isVid = song.coverImage?.match(/\.(mp4|mov|webm)$/i);
                return (
                  <div
                    key={`${song.id}-${index}`}
                    style={{
                      ...itemStyle,
                      background: isQueueActive && index === currentQueueIndex ? 'rgba(129,83,83,0.2)' : 'transparent',
                    }}
                    onClick={() => playQueue(index)}
                  >
                    {isVid ? (
                      <video src={song.coverImage} style={drawerThumbStyle} autoPlay loop muted playsInline />
                    ) : (
                      <img src={song.coverImage} alt={song.title} style={drawerThumbStyle} />
                    )}
                    <span style={{ color: isQueueActive && index === currentQueueIndex ? '#b08a8a' : '#fff', flex: 1, cursor: 'pointer' }}>
                      {song.title}
                    </span>
                    <span style={{ color: '#815353', fontSize: '0.8rem', marginRight: '8px' }}>
                      Vol {song.volume}
                    </span>
                    <button
                      onClick={(e) => { e.stopPropagation(); removeFromQueue(index); }}
                      style={removeBtnStyle}
                    >
                      <FaTimes />
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </>
  );
};

const overlayStyle: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0,0,0,0.5)', zIndex: 999,
};

const drawerStyle: React.CSSProperties = {
  position: 'fixed', top: 0, right: 0, bottom: 0, width: '340px', maxWidth: '90vw',
  background: '#2e2020', zIndex: 1000, transition: 'transform 0.3s ease',
  display: 'flex', flexDirection: 'column', borderLeft: '1px solid #815353',
};

const headerStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  padding: '1rem 1.25rem', borderBottom: '1px solid #815353',
};

const iconBtnStyle: React.CSSProperties = {
  background: 'none', border: 'none', color: '#b08a8a', fontSize: '1.2rem', cursor: 'pointer',
};

const toastStyle: React.CSSProperties = {
  background: '#3d2a2a', color: '#b08a8a', padding: '10px 16px',
  margin: '10px 16px 0', borderRadius: '8px', fontSize: '0.85rem',
  textAlign: 'center', border: '1px solid #815353',
};

const emptyStyle: React.CSSProperties = {
  color: '#815353', textAlign: 'center', padding: '3rem 1rem',
};

const actionsStyle: React.CSSProperties = {
  display: 'flex', gap: '8px', padding: '0.75rem 1.25rem', flexWrap: 'wrap',
};

const actionBtnStyle: React.CSSProperties = {
  padding: '6px 12px', borderRadius: '20px', border: '1px solid #b08a8a',
  background: 'transparent', color: '#b08a8a', fontSize: '0.8rem',
  cursor: 'pointer', display: 'flex', alignItems: 'center',
};

const nameInputContainerStyle: React.CSSProperties = {
  padding: '0 1.25rem 0.75rem', display: 'flex', flexDirection: 'column', gap: '8px',
};

const nameInputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px', borderRadius: '8px', border: 'none',
  background: '#3d2a2a', color: '#fff', fontSize: '0.9rem', boxSizing: 'border-box',
};

const saveConfirmBtnStyle: React.CSSProperties = {
  padding: '6px 16px', borderRadius: '8px', border: 'none',
  background: '#815353', color: '#fff', fontSize: '0.8rem', cursor: 'pointer',
};

const cancelBtnStyle: React.CSSProperties = {
  padding: '6px 12px', borderRadius: '8px', border: '1px solid #815353',
  background: 'transparent', color: '#815353', fontSize: '0.8rem', cursor: 'pointer',
};

const listStyle: React.CSSProperties = {
  flex: 1, overflowY: 'auto', padding: '0.5rem 0',
};

const itemStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', padding: '10px 1.25rem',
  borderBottom: '1px solid rgba(129,83,83,0.2)', transition: 'background 0.2s',
};

const drawerThumbStyle: React.CSSProperties = {
  width: '32px', height: '32px', borderRadius: '4px', objectFit: 'cover',
  marginRight: '10px', flexShrink: 0,
};

const removeBtnStyle: React.CSSProperties = {
  background: 'none', border: 'none', color: '#815353', cursor: 'pointer',
  fontSize: '0.9rem', padding: '4px',
};

export default QueueDrawer;
